import React, { useState, forwardRef, useRef, useImperativeHandle } from "react";
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import { useForm } from "react-hook-form";
import SimpleObjectRenderer from "../aggrid-cell-renderers/simple-object-renderer";
import IncomingClassCategoryTypeEditor from "../aggrid-cell-editors/incoming-classification-category-editor";
import IncomingClassValueEditor from "../aggrid-cell-editors/incoming-classification-value-editor";
import DoubleValRenderer from "../aggrid-cell-renderers/doubleval-renderer";
import ActionsRenderer from "../aggrid-cell-renderers/actions-renderer";
import { useEditableGrid } from './useEditableGrid';

const EMPTY_ROW = {
    classificationValue: { value: undefined, label: undefined },
    classificationCategory: { value: undefined, label: undefined },
    amount: undefined
};

const isValid = (data) => {
    var ans = data.classificationCategory && data.classificationCategory.value != undefined && data.classificationCategory.value != "" &&
    data.classificationValue && data.classificationValue.value != undefined && data.classificationValue.value != "" &&
    data.amount != undefined && data.amount != "";

    return ans;
}


const SaleEntriesClassificationsGrid = forwardRef((props, ref) => {
    const { t } = useTranslation();
    const { setValue } = useForm();
    const [initRowTotals, setInitRowTotals] = useState(0);
    const [classTotals, setClassTotals] = useState(0);
    const { gridApi, hasError, rowStyle, getRowStyle, initGrid, addRow, checkErrors, getItems, setItems } = useEditableGrid({
        isValid,
        emptyRow: EMPTY_ROW,
        globalRows: true,
    });

    const onGridReady = (params) => {
        setInitRowTotals(props.rowTotals);
        initGrid(params, props.extraRowData && props.extraRowData.length > 0 ? props.extraRowData : null);

        if (props.vatCategoryException != undefined) {
            setValue("vatCategoryException", props.vatCategoryException);
        }

        const items = [];
        params.api.forEachNode(node => items.push(node.data));
        items.pop();
        const total = items.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
        if (props.rowTotals != total && !props.isInvoice00_1) {
            props.setRowTotalsConstraintDisplay("unset");
            document.getElementById("onExtras").setAttribute("disabled", "disabled");
        } else {
            props.setRowTotalsConstraintDisplay("none");
            document.getElementById("onExtras").removeAttribute("disabled");
        }
    };

    const onCellValueChanged = (params) => {
        const nodeIndex = params.node.rowIndex;
        const countDisplayedRows = params.api.getDisplayedRowCount() - 1;
        if (nodeIndex == countDisplayedRows && params.newValue != undefined) {
            addRow(params.api);
            params.api.redrawRows();
        }

        const items = [];
        params.api.forEachNode(node => items.push(node.data));
        items.pop();
        let total = 0;
        items.forEach(i => {
            if (i.classificationValue && i.classificationValue.value && !i.classificationValue.value.includes("VAT_")) {
                total += parseFloat(i.amount || 0);
            }
        });
        setClassTotals(total);

        if (params.column.colId === "amount") {
            if (initRowTotals != total) {
                props.setRowTotalsConstraintDisplay("unset");
                document.getElementById("onExtras").setAttribute("disabled", "disabled");
            } else {
                props.setRowTotalsConstraintDisplay("none");
                document.getElementById("onExtras").removeAttribute("disabled");
            }
        }

        checkErrors(params.api);
    };

    useImperativeHandle(ref, () => ({
        getRowsTotals() {
            return classTotals;
        },
        getData() {
            return getItems();
        },
        setData(data) {
            setItems(data);
        },
        showModal() {},
        closeModal() {},
        hasError() {
            return hasError;
        }
    }));

    const deleteRowById = (rowIndex, _api) => {
        if (gridApi != undefined) {
            const items = [];
            gridApi.forEachNode(node => items.push(node.data));
            items.splice(rowIndex, 1);
            gridApi.setRowData(items);
            const dataItems = items.slice(0, -1);
            const total = dataItems.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
            if (props.rowTotals != total) {
                props.setRowTotalsConstraintDisplay("unset");
                document.getElementById("onExtras").setAttribute("disabled", "disabled");
            } else {
                props.setRowTotalsConstraintDisplay("none");
                document.getElementById("onExtras").removeAttribute("disabled");
            }
        }
    };

    return (
        <div className="row">
            <div className="col-md-12">
                <div className="ag-theme-alpine editableSmGrid salesClassicicationsGrid classification_table" style={{ width: '100%' }}>
                    <AgGridReact
                        onGridReady={onGridReady}
                        rowStyle={rowStyle}
                        getRowStyle={getRowStyle}
                        style={{ width: '100%', height: '200px' }}
                        onCellValueChanged={onCellValueChanged}
                        stopEditingWhenCellsLoseFocus={true}
                        borders={true}
                        suppressRowTransform={true}
                        singleClickEdit={true}
                        skipHeaderOnAutoSize={true}
                        defaultColDef={{
                            flex: 1,
                            resizable: true,
                            wrapText: true,
                            suppressMovable: true
                        }}
                        columnDefs={[
                            { field: "classificationValues", hide: true },
                            { field: "classificationCategory", flex: 2, minWidth: 200, headerName: t("incomingClassificationCategoryType"), editable: true, cellRenderer: SimpleObjectRenderer, cellEditor: IncomingClassCategoryTypeEditor, cellRendererParams: { incomeClassCategories: props.incomeClassCategories, myDataInvoiceType: props.myDataInvoiceType } },
                            { field: "classificationValue", flex: 3, minWidth: 200, headerName: t("incomingClassificationValueType"), editable: true, cellRenderer: SimpleObjectRenderer, cellEditor: IncomingClassValueEditor },
                            { field: "amount", flex: 1, minWidth: 100, headerName: t("Amount"), editable: true, cellRenderer: DoubleValRenderer },
                            { field: "name", flex: 1, minWidth: 100, headerName: t("Actions"), cellRenderer: ActionsRenderer, cellRendererParams: { deleteRowById: deleteRowById } },
                        ]}
                    >
                    </AgGridReact>
                </div>
            </div>
            <div><br /></div>
        </div>
    );
});
export default SaleEntriesClassificationsGrid;
